// src/pages/QuranPage.js
import React, { useEffect, useState } from 'react';
import {
  Box, Flex, Text, Button, HStack, NumberInput, NumberInputField, Spinner, Alert, AlertIcon,
  useColorModeValue,
} from '@chakra-ui/react';
import { Link as RouterLink, Navigate, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import QuranShell from '../components/Quran/QuranShell';
import ReaderSettings from '../components/Quran/ReaderSettings';
import { BISMILLAH, toArabicDigits } from '../components/Quran/AyahCard';
import useQuranSettings from '../hooks/useQuranSettings';
import { fetchQuranByPage } from '../api/quranService';
import { getSurah } from '../data/surahMeta';
import { juzOf } from '../data/juzMeta';

const TOTAL_PAGES = 604;

/**
 * One page of the Madinah mushaf, read right to left like the printed copy.
 */
const QuranPage = () => {
  const { page } = useParams();
  const navigate = useNavigate();
  const settings = useQuranSettings();
  const pageNumber = Number(page);
  const [ayahs, setAyahs] = useState(null);
  const [error, setError] = useState(null);
  const [jumpTo, setJumpTo] = useState(String(pageNumber || 1));
  const paperBg = useColorModeValue('#fffdf5', 'gray.800');
  const paperBorder = useColorModeValue('yellow.200', 'gray.600');
  const markerColor = useColorModeValue('teal.700', 'teal.300');

  const valid = Number.isInteger(pageNumber) && pageNumber >= 1 && pageNumber <= TOTAL_PAGES;

  useEffect(() => {
    if (!valid) return undefined;
    let cancelled = false;
    setAyahs(null);
    setError(null);
    setJumpTo(String(pageNumber));
    fetchQuranByPage(pageNumber)
      .then((data) => !cancelled && setAyahs(data.ayahs || []))
      .catch(() => !cancelled && setError('This page could not be loaded.'));
    return () => { cancelled = true; };
  }, [pageNumber, valid]);

  if (!valid) return <Navigate to="/pages/1" replace />;

  const first = ayahs?.[0];
  const surahNames = [...new Set((ayahs || []).map((a) => getSurah(a.surah_id)?.name))].join(' · ');
  const go = (n) => navigate(`/pages/${Math.min(TOTAL_PAGES, Math.max(1, n))}`);

  return (
    <QuranShell>
      <Flex justify="space-between" align="center" mb={4} gap={3} wrap="wrap">
        <Text color="gray.500" fontSize="sm">
          {first ? `${surahNames} · Juz ${juzOf(Number(first.surah_id), Number(first.ayah_index))}` : ' '}
        </Text>
        <HStack as="form" onSubmit={(e) => { e.preventDefault(); go(Number(jumpTo)); }}>
          <Text fontSize="sm">Page</Text>
          <NumberInput size="sm" maxW="80px" min={1} max={TOTAL_PAGES} value={jumpTo} onChange={setJumpTo}>
            <NumberInputField aria-label="Page number" />
          </NumberInput>
          <Text fontSize="sm" color="gray.500">/ {TOTAL_PAGES}</Text>
          <Button size="sm" type="submit" variant="outline">Go</Button>
        </HStack>
      </Flex>

      <ReaderSettings settings={settings} showReciter={false} showTranslationPicker={false} />

      {error && <Alert status="error" borderRadius="lg"><AlertIcon />{error}</Alert>}
      {!ayahs && !error && <Flex justify="center" py={16}><Spinner size="xl" color="teal.500" /></Flex>}

      {ayahs && (
        <Box
          maxW="820px" mx="auto" bg={paperBg} border="1px solid" borderColor={paperBorder} borderRadius="lg"
          boxShadow="md" px={{ base: 5, md: 12 }} py={{ base: 6, md: 10 }} dir="rtl"
        >
          <Text as="div" fontFamily={settings.fontFamily} fontSize={{ base: '2xl', md: settings.arabicSize }} lineHeight="2.4" textAlign="justify">
            {ayahs.map((ayah) => {
              const surahId = Number(ayah.surah_id);
              const startsSurah = Number(ayah.ayah_index) === 1;
              return (
                <React.Fragment key={ayah.ayah_key}>
                  {startsSurah && (
                    <Box textAlign="center" my={4}>
                      <Box
                        display="inline-block" px={8} py={1} borderY="2px solid" borderColor={paperBorder}
                        fontFamily="'Amiri Quran', serif" fontSize="xl"
                      >
                        {getSurah(surahId)?.arabicName}
                      </Box>
                      {surahId !== 1 && surahId !== 9 && <Box fontSize="0.9em">{BISMILLAH}</Box>}
                    </Box>
                  )}
                  {ayah.text}
                  <Text as="span" mx={1} fontSize="0.75em" color={markerColor} whiteSpace="nowrap">
                    ﴿{toArabicDigits(ayah.ayah_index)}﴾
                  </Text>{' '}
                </React.Fragment>
              );
            })}
          </Text>
          <Text textAlign="center" fontSize="sm" color="gray.500" mt={6}>{toArabicDigits(pageNumber)}</Text>
        </Box>
      )}

      {/* The mushaf is read right to left, so the next page sits on the left */}
      <Flex justify="space-between" mt={8} maxW="820px" mx="auto">
        <Button as={RouterLink} to={`/pages/${pageNumber + 1}`} variant="outline" leftIcon={<ChevronLeft size={16} />}
          isDisabled={pageNumber >= TOTAL_PAGES} pointerEvents={pageNumber >= TOTAL_PAGES ? 'none' : undefined}>
          Next page
        </Button>
        <Button as={RouterLink} to={`/pages/${pageNumber - 1}`} variant="outline" rightIcon={<ChevronRight size={16} />}
          isDisabled={pageNumber <= 1} pointerEvents={pageNumber <= 1 ? 'none' : undefined}>
          Previous page
        </Button>
      </Flex>
    </QuranShell>
  );
};

export default QuranPage;
